import React from 'react';

import { DialogActions, DialogContent, InputLabel } from '@mui/material';
import { Formik } from 'formik';
import { AlineButton, AlineDialog, FormikAlineInput, FormikAlineTextarea } from 'src/components';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

const useStyles = makeStyles()(theme => ({
  container: {
    width: 566,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(1),
  },
  saveButton: {
    marginLeft: theme.spacing(1),
  },
}));

export interface PartyForm {
  name: string;
  description?: string;
}
export const PartyFormSchema = Yup.object<PartyForm>({
  description: Yup.string(),
  name: Yup.string().required('Required'),
});

interface PartyDialogProps {
  handleClose: () => void;
  handleSubmit: (form: PartyForm) => void;
  open: boolean;
  initialValues?: PartyForm;
  submitText?: string;
  title?: string;
}

export function PartyDialog(props: PartyDialogProps): JSX.Element {
  const { classes } = useStyles();
  const {
    handleClose,
    handleSubmit,
    initialValues = { description: '', name: '' },
    open,
    submitText = 'Create',
    title = 'Create party',
  } = props;

  return (
    <AlineDialog
      classes={{ paperWidthMd: classes.container }}
      open={open}
      title={title}
      onClose={handleClose}
    >
      <Formik<PartyForm>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={PartyFormSchema}
        onSubmit={async form => {
          await handleSubmit(form);
          handleClose();
        }}
      >
        {({ handleReset, handleSubmit, isSubmitting }): JSX.Element => {
          return (
            <form onReset={handleReset} onSubmit={handleSubmit}>
              <DialogContent className={classes.dialogContent}>
                <InputLabel className={classes.inputLabel}>Legal Name</InputLabel>
                <FormikAlineInput
                  autoFocus
                  data-cy="party-name-input"
                  fieldName="name"
                  placeholder="Add legal name..."
                />
                <InputLabel className={classes.inputLabel}>Description</InputLabel>
                <FormikAlineTextarea
                  data-cy="party-description-input"
                  fieldName="description"
                  maxRows={5}
                  minRows={3}
                  placeholder="Add description..."
                />
              </DialogContent>
              <DialogActions>
                <AlineButton color="primary" variant="outlined" onClick={handleClose}>
                  Cancel
                </AlineButton>
                <AlineButton
                  className={classes.saveButton}
                  color="primary"
                  loading={isSubmitting}
                  type="submit"
                  variant="contained"
                >
                  {submitText}
                </AlineButton>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </AlineDialog>
  );
}
